import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import MediumStyleArticle from './MediumStyleArticle.jsx';
import CommentableContentRenderer from '../components/Comments/CommentableContentRenderer.jsx';
import CommentSidebar from '../components/Comments/CommentSidebar.jsx';
import { useComments } from '../hooks/useComments.js';
import { ENDPOINTS } from '../Utils/APIConstants.jsx';
import { DarkModeProvider, useDarkMode } from '../contexts/DarkModeContext';
import { DebugProvider } from '../contexts/DebugContext';
import { DebugApiResponse } from '../components/Debug/DebugMenu';
import DarkModeToggle from '../components/UI/DarkModeToggle';
import { LinkService } from '../Services/LinkService.jsx';
import { ApiService } from '../Services/APIService.jsx';
import sessionTracker from '../Utils/SessionTracker.js';
import MetaTagManager from '../components/SEO/MetaTagManager.jsx';
import ErrorBoundary from '../components/ErrorBoundary.jsx';
import { StorageUtils } from '../Utils/StorageUtils.jsx';
import SummaryLoadingOverlay from '../components/Loading/SummaryLoadingOverlay.jsx';
import AccessControlModal from '../components/Modals/AccessControlModal.jsx';
import '../CSS/DarkMode.css';

// Function to track summary page visits
const trackSummaryPageVisit = async (slug) => {
  try {
    console.log('=== SUMMARY PAGE VISIT TRACKING START ===');
    console.log('📊 Tracking summary page visit for slug:', slug);
    console.log('   BASE_URL:', 'https://trackable-gpt-backend.onrender.com/');
    console.log('   Full endpoint URL:', `https://trackable-gpt-backend.onrender.com/links/summary-clicks`);
    
    // Get current session data from SessionTracker
    const sessionData = sessionTracker.getSessionData();
    console.log('🔑 Current session data:', {
      sessionId: sessionData.sessionId,
      visitorId: sessionData.visitorId,
      sessionStart: sessionData.sessionStart?.toISOString(),
      sessionEnd: sessionData.sessionEnd?.toISOString(),
      isActive: sessionTracker.isSessionActive()
    });
    
    const pageViewData = { 
      slug, 
      action: 'page_view',
      sessionId: sessionData.sessionId,
      visitorId: sessionData.visitorId,
      sessionStart: sessionData.sessionStart?.toISOString(),
      sessionEnd: sessionData.sessionEnd?.toISOString()
    };
    console.log('   Sending page view data:', pageViewData);
    
    const response = await LinkService.logSummaryClick(pageViewData);
    console.log('✅ Summary page visit logged successfully');
    console.log('   Response:', response);
    console.log('=== SUMMARY PAGE VISIT TRACKING END ===');
  } catch (error) {
    console.error('❌ Failed to track summary page visit:', error);
    // Don't throw - we don't want tracking failures to break the page
  }
};

// Function to parse summary blocks from the summary text
const parseSummaryBlocks = (summaryText) => {
  if (!summaryText) return [];
  
  try {
    // Try to parse as JSON first (new format)
    if (summaryText.trim().startsWith('[') || summaryText.trim().startsWith('{')) {
      const parsed = JSON.parse(summaryText);
      
      // If it's an array, return it
      if (Array.isArray(parsed)) {
        return parsed;
      }
      
      // If it's an object with contentBlocks property (browser extension format)
      if (parsed && typeof parsed === 'object' && Array.isArray(parsed.contentBlocks)) {
        console.log('✅ Extracted contentBlocks array from wrapper object');
        return parsed.contentBlocks;
      }
      
      // If it's a single block object, wrap it
      if (parsed && typeof parsed === 'object') {
        return [parsed];
      }
    }
    
    // Fallback: split by double newlines and create paragraph blocks
    const paragraphs = summaryText.split('\n\n').filter(p => p.trim());
    return paragraphs.map(text => ({
      type: 'paragraph',
      text: text.trim()
    }));
  } catch (error) {
    console.error('Error parsing summary blocks:', error);
    // Fallback: create a single paragraph block
    return [{
      type: 'paragraph',
      text: summaryText
    }];
  }
};

// Function to calculate read time
const calculateReadTime = (text) => {
  const wordsPerMinute = 200;
  const wordCount = text.split(/\s+/).length;
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  return `${minutes} min read`;
};

function SummaryDynamicViewer() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerationError, setRegenerationError] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [showRegenerationModal, setShowRegenerationModal] = useState(false);
  const [showAccessControlModal, setShowAccessControlModal] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false); // Mock data for now
  const [viewers, setViewers] = useState([]); // Mock data for now
  const [userAccessStatus, setUserAccessStatus] = useState(null); // 'owner', 'viewer', or null for public
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const [highlightedCommentIds, setHighlightedCommentIds] = useState([]);

  // Check if current user owns the summary
  const currentUserData = StorageUtils.getUserData();
  const currentUserEmail = currentUserData?.user?.email;
  const currentUserId = currentUserData?.user?.id;
  const isOwner = currentUserEmail && data?.author?.email && currentUserEmail === data.author.email;
  
  // Set auth token on mount so API requests include JWT
  useEffect(() => {
    const token = StorageUtils.getUserData()?.token;
    if (token) {
      ApiService.setAuthToken(token);
      console.log('🔐 Auth token set on APIService:', token.substring(0, 30) + '...');
    } else {
      console.log('⚠️ No auth token found in storage');
    }
  }, []);
  
  // Determine user access status
  useEffect(() => {
    if (isOwner) {
      setUserAccessStatus('owner');
    } else if (currentUserEmail && viewers.some(v => v.email?.toLowerCase() === currentUserEmail.toLowerCase())) {
      setUserAccessStatus('viewer');
    } else if (!isPrivate) {
      setUserAccessStatus(null); // Public, no special status needed
    } else {
      setUserAccessStatus(null); // Private but no access
    }
  }, [isOwner, currentUserEmail, viewers, isPrivate]);
  
  // Debug logging for ownership check (can be removed in production)
  console.log('🔍 Ownership Debug:', {
    currentUserEmail,
    authorEmail: data?.author?.email,
    isOwner,
    userAccessStatus
  });

  // Initialize comments hook
  const {
    comments,
    loading: commentsLoading,
    error: commentsError,
    stats,
    createComment,
    updateComment,
    deleteComment,
    replyToComment
  } = useComments(data?.id);

  // Function to handle summary regeneration
  const handleRegenerateSummary = async () => {
    if (!slug || !isOwner) {
      console.warn('Cannot regenerate: missing slug or user is not owner');
      return;
    }

    setIsRegenerating(true);
    setRegenerationError(null);

    try {
      console.log('🔄 Starting summary regeneration for slug:', slug);
      console.log('🔑 Current user token:', currentUserData?.token ? 'exists' : 'missing');
      console.log('🔑 Token preview:', currentUserData?.token ? currentUserData.token.substring(0, 20) + '...' : 'none');
      
      const regeneratedData = await LinkService.regenerateSummary(slug);
      
      console.log('✅ Summary regenerated successfully');
      console.log('📦 Regenerated data structure:', regeneratedData);
      console.log('📦 Data type:', typeof regeneratedData);
      console.log('📦 Has summary field:', 'summary' in regeneratedData);
      
      setData(regeneratedData);
      
    } catch (error) {
      console.error('❌ Failed to regenerate summary:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      setRegenerationError(error.message || 'Failed to regenerate summary');
    } finally {
      setIsRegenerating(false);
    }
  };

  // Access control handlers
  const handleTogglePrivacy = async (newIsPrivate) => {
    try {
      await LinkService.toggleSummaryPrivacy(slug, newIsPrivate);
      setIsPrivate(newIsPrivate);
      console.log('Privacy toggled:', newIsPrivate);
    } catch (error) {
      console.error('Failed to toggle privacy:', error);
      alert('Failed to update privacy setting');
    }
  };

  const handleAddViewer = async (email) => {
    try {
      await LinkService.addSummaryViewer(slug, email);
      setViewers([...viewers, { email }]);
      console.log('Viewer added:', email);
    } catch (error) {
      console.error('Failed to add viewer:', error);
      alert(error.response?.data?.error || 'Failed to add viewer');
    }
  };

  const handleRemoveViewer = async (email) => {
    try {
      await LinkService.removeSummaryViewer(slug, email);
      setViewers(viewers.filter(v => v.email !== email));
      console.log('Viewer removed:', email);
    } catch (error) {
      console.error('Failed to remove viewer:', error);
      alert('Failed to remove viewer');
    }
  };

  useEffect(() => {
    if (!slug) {
      console.error("No slug provided in URL.");
      setLoading(false);
      return;
    }

    console.log("Fetching summary for slug:", slug);
    console.log("Using ApiService with dynamic endpoint:", `${ENDPOINTS.SUMMARY}/${slug}`);

    // Ensure auth token is set before fetching
    const token = StorageUtils.getUserData()?.token;
    if (token) {
      ApiService.setAuthToken(token);
      console.log('🔐 Auth token set before fetch:', token.substring(0, 30) + '...');
    } else {
      console.log('⚠️ No auth token available for fetch');
    }

    ApiService.get(`${ENDPOINTS.SUMMARY}/${slug}`)
      .then((response) => {
        console.log('Response status:', response.status);
        return response.data;
      })
      .then(async (json) => {
        if (!json || typeof json !== 'object' || Object.keys(json).length === 0) {
          throw new Error("Empty or invalid JSON returned");
        }
        console.log('Fetched summary payload:', json);
        setData(json);
        
        // Track page visit when summary loads successfully
        await trackSummaryPageVisit(slug);
      })
      .catch((err) => {
        console.error('Fetch error:', err);
        
        if (err.response?.status === 403) {
          // Show private summary message
          setData({ 
            isPrivate: true, 
            error: 'This summary is private',
            message: err.response.data.message || 'Please sign in with an authorized email to view this summary'
          });
        } else {
          setData(null);
        }
      })
      .finally(() => setLoading(false));
  }, [slug]);

  // Handle comment creation
  const handleCommentCreate = async (commentData) => {
    console.log('🎯 handleCommentCreate called with:', commentData);
    console.log('📊 Current user ID:', currentUserId);
    console.log('📝 Summary ID:', data?.id);
    
    if (!currentUserId) {
      alert('Please log in to add comments');
      return;
    }

    try {
      const fullCommentData = {
        summaryId: data.id,
        userId: currentUserId,
        ...commentData
      };
      console.log('📤 Sending comment data:', fullCommentData);
      
      const result = await createComment(fullCommentData);
      console.log('✅ Comment created successfully:', result);
    } catch (error) {
      console.error('❌ Error creating comment:', error);
      alert('Failed to create comment. Please try again.');
    }
  };

  // Handle comment click
  const handleCommentClick = (comment) => {
    setShowComments(true);
    
    // Find all comments for this specific text range
    const commentsForRange = comments.filter(c => 
      c.blockIndex === comment.blockIndex &&
      c.startOffset === comment.startOffset &&
      c.endOffset === comment.endOffset
    );
    
    // Set highlighted comment IDs
    setHighlightedCommentIds(commentsForRange.map(c => c.id));
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowComments(false);
      }
      if (e.key === 'c' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setShowComments(!showComments);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showComments]);

  // Load viewers when access control modal opens
  useEffect(() => {
    if (showAccessControlModal && isOwner) {
      LinkService.getSummaryViewers(slug)
        .then(data => {
          setViewers(data.viewers || []);
          setIsPrivate(data.isPrivate);
        })
        .catch(err => console.error('Failed to load viewers:', err));
    }
  }, [showAccessControlModal, slug, isOwner]);

  if (loading) return (
    <div className={`summary-screen-container loading-text ${isDarkMode ? 'dark-mode-active' : ''}`} style={{ 
      color: 'var(--text-primary)', 
      backgroundColor: 'var(--bg-primary)',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.2rem'
    }}>
      Loading...
    </div>
  );
  
  // Handle private summary access denied
  if (data?.isPrivate) {
    return (
      <div className={`summary-screen-container ${isDarkMode ? 'dark-mode-active' : ''}`} style={{ 
        color: isDarkMode ? '#f9fafb' : '#111827', 
        backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          textAlign: 'center',
          maxWidth: '500px',
          padding: '40px',
          backgroundColor: isDarkMode ? '#374151' : '#f9fafb',
          borderRadius: '12px',
          border: `1px solid ${isDarkMode ? '#4b5563' : '#e5e7eb'}`
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '20px',
            color: isDarkMode ? '#6b7280' : '#9ca3af'
          }}>
            🔒
          </div>
          <h1 style={{
            fontSize: '24px',
            fontWeight: '600',
            marginBottom: '12px',
            color: isDarkMode ? '#f9fafb' : '#111827'
          }}>
            {data.error || 'This summary is private'}
          </h1>
          <p style={{
            fontSize: '16px',
            color: isDarkMode ? '#d1d5db' : '#6b7280',
            marginBottom: '24px',
            lineHeight: '1.5'
          }}>
            {data.message || 'Please sign in with an authorized email to view this summary.'}
          </p>
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center'
          }}>
            <button
              onClick={() => window.location.href = '/login'}
              style={{
                padding: '12px 24px',
                backgroundColor: isDarkMode ? '#3b82f6' : '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = isDarkMode ? '#2563eb' : '#1d4ed8';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = isDarkMode ? '#3b82f6' : '#2563eb';
              }}
            >
              Sign In
            </button>
            <button
              onClick={() => window.location.href = '/'}
              style={{
                padding: '12px 24px',
                backgroundColor: 'transparent',
                color: isDarkMode ? '#9ca3af' : '#6b7280',
                border: `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = isDarkMode ? '#374151' : '#f3f4f6';
                e.target.style.borderColor = isDarkMode ? '#6b7280' : '#9ca3af';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.borderColor = isDarkMode ? '#4b5563' : '#d1d5db';
              }}
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    console.warn("Received null or empty data from API");
    return (
      <div className={`summary-screen-container error-text ${isDarkMode ? 'dark-mode-active' : ''}`} style={{ 
        color: 'var(--text-primary)', 
        backgroundColor: 'var(--bg-primary)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.2rem'
      }}>
        Error loading summary (data was null or empty)
      </div>
    );
  }

  console.log("📦 Rendering SummaryDynamicViewer. Full data object:", data);
  console.log("🧱 Blocks field (data.contentBlocks):", data.contentBlocks);

  // Extract title from summary content if not in root data
  const extractTitleFromSummary = (summaryText) => {
    try {
      const blocks = parseSummaryBlocks(summaryText);
      // Look for the first heading block
      for (const block of blocks) {
        if (block.type === 'heading' && block.level === 1 && block.text) {
          return block.text;
        }
      }
      // Fallback: look for any heading
      for (const block of blocks) {
        if (block.type === 'heading' && block.text) {
          return block.text;
        }
      }
      return null;
    } catch (error) {
      console.error('Error extracting title from summary:', error);
      return null;
    }
  };

  // Get title from data or extract from summary
  const title = data.title || extractTitleFromSummary(data.summary) || 'AI Conversation Summary';
  console.log('📝 Using title:', title);

  // Prepare meta tag data
  const summaryUrl = `https://threadshare.ai/summary/${slug}`;
  const socialImageUrl = `https://threadshare.ai/assets/social-card-${slug}.png`;
  const readTime = data.readTime || calculateReadTime(data.summary);
  const description = data.subtitle || data.shortSummaryDescription || `${title.substring(0, 160)}... | Shared on ThreadShare`;

  return (
    <div className={`summary-screen-container ${isDarkMode ? 'dark-mode-active' : ''}`}>
      {/* Dynamic Meta Tags */}
      <MetaTagManager
        title={`${title} | ThreadShare`}
        description={description}
        image={socialImageUrl}
        url={summaryUrl}
        type="article"
        author={data.author?.name || 'Anonymous'}
        publishedTime={data.createdAt}
        readTime={readTime}
      />
      
      {/* Debug Response Display */}
      <DebugApiResponse 
        response={data}
        title="API Response Debug"
        position="top-right"
        maxHeight="400px"
        maxWidth="500px"
      />
      
      {/* Toolbar with Comments, Refresh, and Dark Mode */}
      <div 
        className="toolbar-container"
        style={{ 
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          background: isDarkMode ? '#2a2a2a' : '#ffffff',
          border: 'none',
          borderRadius: '16px',
          padding: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000
        }}
      >
        <button
          onClick={() => setShowComments(!showComments)}
          style={{
            background: isDarkMode ? '#333' : '#f5f5f5',
            border: 'none',
            borderRadius: '12px',
            padding: '12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isDarkMode ? '#fff' : '#333',
            transition: 'all 0.2s ease',
            minWidth: '44px',
            minHeight: '44px',
            position: 'relative'
          }}
          title=""
        >
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          
          {/* Custom Tooltip */}
          <div className="custom-tooltip" style={{
            position: 'absolute',
            bottom: 'calc(100% + 12px)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: isDarkMode ? '#1f2937' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: '500',
            color: isDarkMode ? '#f9fafb' : '#111827',
            whiteSpace: 'nowrap',
            opacity: 0,
            visibility: 'hidden',
            transition: 'all 0.2s ease',
            zIndex: 1001,
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            pointerEvents: 'none'
          }}>
            {stats.totalComments || 0} Comments • {showComments ? 'Hide' : 'Show'} Comments
            <div style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: `6px solid ${isDarkMode ? '#1f2937' : '#ffffff'}`
            }}></div>
          </div>
        </button>

        {isOwner && (
          <button
            onClick={() => setShowAccessControlModal(true)}
            style={{
              background: isDarkMode ? '#333' : '#f5f5f5',
              border: 'none',
              borderRadius: '12px',
              padding: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isDarkMode ? '#fff' : '#333',
              transition: 'all 0.2s ease',
              minWidth: '44px',
              minHeight: '44px',
              position: 'relative',
              boxShadow: isDarkMode ? '0 0 0 1px rgba(255, 215, 0, 0.3)' : '0 0 0 1px rgba(255, 215, 0, 0.2)'
            }}
            title=""
          >
            {/* Lock icon - changes based on isPrivate state */}
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              {isPrivate ? (
                // Locked icon
                <>
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </>
              ) : (
                // Unlocked icon
                <>
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 9.9-1"/>
                </>
              )}
            </svg>
            
            {/* Custom Tooltip */}
            <div className="custom-tooltip" style={{
              position: 'absolute',
              bottom: 'calc(100% + 12px)',
              left: '50%',
              transform: 'translateX(-50%)',
              background: isDarkMode ? '#1f2937' : '#ffffff',
              border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
              borderRadius: '12px',
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: '500',
              color: isDarkMode ? '#f9fafb' : '#111827',
              whiteSpace: 'nowrap',
              opacity: 0,
              visibility: 'hidden',
              transition: 'all 0.2s ease',
              zIndex: 1001,
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
              pointerEvents: 'none'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                Manage access (Author only)
              </div>
              <div style={{
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderTop: `6px solid ${isDarkMode ? '#1f2937' : '#ffffff'}`
              }}></div>
            </div>
          </button>
        )}

        <button
          onClick={() => {
            const summaryUrl = `${window.location.origin}/summary/${slug}`;
            if (navigator.share) {
              navigator.share({
                title: title,
                text: `Check out this AI summary: ${title}`,
                url: summaryUrl
              }).catch(console.error);
            } else {
              // Fallback: copy to clipboard
              navigator.clipboard.writeText(summaryUrl).then(() => {
                // You could add a toast notification here
                console.log('URL copied to clipboard');
              }).catch(console.error);
            }
          }}
          style={{
            background: isDarkMode ? '#333' : '#f5f5f5',
            border: 'none',
            borderRadius: '12px',
            padding: '12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isDarkMode ? '#fff' : '#333',
            transition: 'all 0.2s ease',
            minWidth: '44px',
            minHeight: '44px',
            position: 'relative'
          }}
          title=""
        >
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <circle cx="18" cy="5" r="3"/>
            <circle cx="6" cy="12" r="3"/>
            <circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
          
          {/* Custom Tooltip */}
          <div className="custom-tooltip" style={{
            position: 'absolute',
            bottom: 'calc(100% + 12px)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: isDarkMode ? '#1f2937' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: '500',
            color: isDarkMode ? '#f9fafb' : '#111827',
            whiteSpace: 'nowrap',
            opacity: 0,
            visibility: 'hidden',
            transition: 'all 0.2s ease',
            zIndex: 1001,
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            pointerEvents: 'none'
          }}>
            Share summary
            <div style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: `6px solid ${isDarkMode ? '#1f2937' : '#ffffff'}`
            }}></div>
          </div>
        </button>
        
        <div style={{
          background: isDarkMode ? '#333' : '#f5f5f5',
          border: 'none',
          borderRadius: '12px',
          padding: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '44px',
          minHeight: '44px'
        }}>
          <DarkModeToggle />
        </div>
      </div>

      {/* Regeneration Error Message */}
      {regenerationError && (
        <div style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          background: '#ff4444',
          color: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          zIndex: 1001,
          maxWidth: '300px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>Regeneration Failed</div>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>{regenerationError}</div>
          <button
            onClick={() => setRegenerationError(null)}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '18px',
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>
      )}
      
      <MediumStyleArticle
        title={title}
        subtitle={data.subtitle}
        author={data.author?.name || "Anonymous"}
        authorProfileImage={data.author?.profile_image}
        date={new Date(data.createdAt).toDateString()}
        readTime={data.readTime || calculateReadTime(data.summary)}
        originalUrl={data.url}
        slug={slug}
        refreshButton={null}
      >
        <CommentableContentRenderer 
          blocks={parseSummaryBlocks(data.summary)} 
          comments={comments}
          onCommentCreate={handleCommentCreate}
          onCommentClick={handleCommentClick}
          currentUserId={currentUserId}
          isCommentingEnabled={!!currentUserId}
        />
      </MediumStyleArticle>

      {/* Comment Sidebar */}
      <CommentSidebar
        comments={comments}
        stats={stats}
        onReply={replyToComment}
        onEdit={updateComment}
        onDelete={deleteComment}
        currentUserId={currentUserId}
        isVisible={showComments}
        onClose={() => {
          setShowComments(false);
          setHighlightedCommentIds([]); // Clear highlighting when sidebar closes
        }}
        highlightedComments={highlightedCommentIds}
        isDarkMode={isDarkMode}
      />

      {/* Summary Loading Overlay */}
      {isRegenerating && <SummaryLoadingOverlay isDarkMode={isDarkMode} />}

      {/* Access Control Modal */}
      {showAccessControlModal && (
        <AccessControlModal
          isVisible={showAccessControlModal}
          onClose={() => setShowAccessControlModal(false)}
          isPrivate={isPrivate}
          onTogglePrivacy={handleTogglePrivacy}
          viewers={viewers}
          onAddViewer={handleAddViewer}
          onRemoveViewer={handleRemoveViewer}
          ownerEmail={currentUserEmail}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Access Status Indicator */}
      {(userAccessStatus === 'owner' || userAccessStatus === 'viewer') && (
        <div style={{
          position: 'fixed',
          bottom: '90px',
          right: '20px',
          backgroundColor: userAccessStatus === 'owner' 
            ? (isDarkMode ? '#b8860b' : '#ffd700')
            : (isDarkMode ? '#3b82f6' : '#dbeafe'),
          border: userAccessStatus === 'owner'
            ? `1px solid ${isDarkMode ? '#8b6914' : '#fbbf24'}`
            : `1px solid ${isDarkMode ? '#1e40af' : '#93c5fd'}`,
          borderRadius: '12px',
          padding: '8px 12px',
          fontSize: '12px',
          fontWeight: '500',
          color: userAccessStatus === 'owner'
            ? (isDarkMode ? '#111827' : '#111827')
            : (isDarkMode ? '#dbeafe' : '#1e40af'),
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          zIndex: 999,
          animation: 'slideInUp 0.3s ease-out'
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            {userAccessStatus === 'owner' ? (
              // Crown icon for owner
              <path d="M12 1l3 8h8L17 14l3 8-8-6-8 6 3-8-6-5h8l3-8z" />
            ) : (
              // Eye icon for viewer
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
            )}
          </svg>
          <span>
            {userAccessStatus === 'owner' ? 'You are the Author' : 'You have Viewer access'}
          </span>
        </div>
      )}

      {/* Regeneration Confirmation Modal */}
      {showRegenerationModal && (
        <div 
          className="modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowRegenerationModal(false);
            }
          }}
        >
          <div 
            className="modal-content"
            style={{
              background: isDarkMode ? '#1f2937' : '#ffffff',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '480px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
              animation: 'modalSlideIn 0.2s ease-out'
            }}
          >
            {/* Modal Header */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '8px'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: isDarkMode ? '#374151' : '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg 
                    width="24" 
                    height="24" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke={isDarkMode ? '#d1d5db' : '#6b7280'} 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/>
                  </svg>
                </div>
                <div>
                  <h2 style={{
                    margin: 0,
                    fontSize: '20px',
                    fontWeight: '600',
                    color: isDarkMode ? '#f9fafb' : '#111827'
                  }}>
                    Regenerate Summary?
                  </h2>
                  <p style={{
                    margin: '4px 0 0 0',
                    fontSize: '14px',
                    color: isDarkMode ? '#9ca3af' : '#6b7280'
                  }}>
                    This action cannot be undone
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{ marginBottom: '32px' }}>
              <p style={{
                margin: '0 0 16px 0',
                fontSize: '16px',
                lineHeight: '1.6',
                color: isDarkMode ? '#e5e7eb' : '#374151'
              }}>
                Regenerating this summary will create a new AI-generated version with updated content.
              </p>
              
              <div style={{
                background: isDarkMode ? '#374151' : '#f3f4f6',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: isDarkMode ? '#4b5563' : '#d1d5db',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: '2px'
                  }}>
                    <svg 
                      width="12" 
                      height="12" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke={isDarkMode ? '#9ca3af' : '#6b7280'} 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                  </div>
                  <div>
                    <h4 style={{
                      margin: '0 0 4px 0',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: isDarkMode ? '#f3f4f6' : '#374151'
                    }}>
                      All comments will be permanently deleted
                    </h4>
                    <p style={{
                      margin: 0,
                      fontSize: '14px',
                      lineHeight: '1.5',
                      color: isDarkMode ? '#d1d5db' : '#6b7280'
                    }}>
                      This includes all replies and conversation threads. Comments cannot be recovered once the summary is regenerated.
                    </p>
                  </div>
                </div>
              </div>

              <div style={{
                background: isDarkMode ? '#374151' : '#f3f4f6',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: isDarkMode ? '#4b5563' : '#d1d5db',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: '2px'
                  }}>
                    <svg 
                      width="12" 
                      height="12" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke={isDarkMode ? '#9ca3af' : '#6b7280'} 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <div>
                    <h4 style={{
                      margin: '0 0 4px 0',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: isDarkMode ? '#f3f4f6' : '#374151'
                    }}>
                      New content will be generated
                    </h4>
                    <p style={{
                      margin: 0,
                      fontSize: '14px',
                      lineHeight: '1.5',
                      color: isDarkMode ? '#d1d5db' : '#6b7280'
                    }}>
                      The AI will create a fresh summary based on the original conversation, which may differ from the current version.
                    </p>
                  </div>
                </div>
              </div>

              <div style={{
                background: isDarkMode ? '#374151' : '#f3f4f6',
                borderRadius: '12px',
                padding: '16px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: isDarkMode ? '#4b5563' : '#d1d5db',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: '2px'
                  }}>
                    <svg 
                      width="12" 
                      height="12" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke={isDarkMode ? '#9ca3af' : '#6b7280'} 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <div>
                    <h4 style={{
                      margin: '0 0 4px 0',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: isDarkMode ? '#f3f4f6' : '#374151'
                    }}>
                      Uses original AI source
                    </h4>
                    <p style={{
                      margin: 0,
                      fontSize: '14px',
                      lineHeight: '1.5',
                      color: isDarkMode ? '#d1d5db' : '#6b7280'
                    }}>
                      The regeneration will use the same original AI link (Gemini, ChatGPT, etc.) that was provided when the summary was first created.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowRegenerationModal(false)}
                style={{
                  background: 'transparent',
                  border: `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: isDarkMode ? '#9ca3af' : '#6b7280',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = isDarkMode ? '#374151' : '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowRegenerationModal(false);
                  handleRegenerateSummary();
                }}
                disabled={isRegenerating}
                style={{
                  background: isDarkMode ? '#374151' : '#111827',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#ffffff',
                  cursor: isRegenerating ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: isRegenerating ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isRegenerating) {
                    e.target.style.background = isDarkMode ? '#4b5563' : '#374151';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isRegenerating) {
                    e.target.style.background = isDarkMode ? '#374151' : '#111827';
                  }
                }}
              >
                {isRegenerating ? 'Regenerating...' : 'Yes, Regenerate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Wrap the component with ErrorBoundary, DarkModeProvider and DebugProvider
export default function SummaryDynamicViewerWithCommentsProvider() {
  return (
    <ErrorBoundary>
      <DebugProvider>
        <DarkModeProvider>
          <SummaryDynamicViewer />
        </DarkModeProvider>
      </DebugProvider>
    </ErrorBoundary>
  );
}
